import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Pipe({
  name: 'markdown'
})
export class MarkdownPipe implements PipeTransform {
  constructor(private sanitizer: DomSanitizer) {}

  transform(value: string): SafeHtml {
    if (!value) return '';

    let html = value;

    // Convert headers (order matters: ### before ## before #)
    html = html.replace(/^#### (.*$)/gim, '<h4>$1</h4>');
    html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
    html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
    html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');

    // Convert bold
    html = html.replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>');

    // Convert italic
    html = html.replace(/\*(.*?)\*/gim, '<em>$1</em>');

    // Convert code blocks
    html = html.replace(/```([^`]+)```/gim, '<pre><code>$1</code></pre>');

    // Convert inline code
    html = html.replace(/`([^`]+)`/gim, '<code>$1</code>');

    // Convert tables
    html = this.convertTables(html);

    // Convert unordered lists
    html = this.convertUnorderedLists(html);

    // Convert ordered lists
    html = this.convertOrderedLists(html);

    // Convert horizontal rules
    html = html.replace(/^---$/gim, '<hr>');

    // Convert line breaks (paragraphs)
    html = html.split('\n\n').map(para => {
      para = para.trim();
      if (!para) return '';
      // Don't wrap if already wrapped in block element
      if (para.startsWith('<h') ||
          para.startsWith('<pre') ||
          para.startsWith('<ul') ||
          para.startsWith('<ol') ||
          para.startsWith('<table') ||
          para.startsWith('<hr')) {
        return para;
      }
      return `<p>${para}</p>`;
    }).join('\n');

    // Clean up extra whitespace
    html = html.replace(/\n/g, ' ').replace(/\s+/g, ' ');

    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  private convertTables(html: string): string {
    const lines = html.split('\n');
    let inTable = false;
    let tableHtml = '';
    const result: string[] = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      if (line.startsWith('|') && line.endsWith('|')) {
        if (!inTable) {
          inTable = true;
          tableHtml = '<table>';
        }

        // Skip separator rows (|---|---|)
        if (line.match(/^\|[\s\-:]+\|$/)) {
          continue;
        }

        const cells = line.split('|').filter(c => c.trim());
        const isHeader = !tableHtml.includes('<tbody>') && (i === 0 || (lines[i + 1]?.trim().match(/^\|[\s\-:]+\|$/) != null));
        const cellTag = isHeader ? 'th' : 'td';

        if (cellTag === 'th' && !tableHtml.includes('<thead>')) {
          tableHtml += '<thead><tr>';
        } else if (cellTag === 'td' && !tableHtml.includes('<tbody>')) {
          tableHtml += '</thead><tbody><tr>';
        } else {
          tableHtml += '<tr>';
        }

        cells.forEach(cell => {
          tableHtml += `<${cellTag}>${cell.trim()}</${cellTag}>`;
        });

        tableHtml += '</tr>';
      } else {
        if (inTable) {
          tableHtml += '</tbody></table>';
          result.push(tableHtml);
          tableHtml = '';
          inTable = false;
        }
        result.push(line);
      }
    }

    if (inTable) {
      tableHtml += '</tbody></table>';
      result.push(tableHtml);
    }

    return result.join('\n');
  }

  private convertUnorderedLists(html: string): string {
    const lines = html.split('\n');
    let inList = false;
    const result: string[] = [];

    for (const line of lines) {
      const trimmed = line.trim();
      if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
        if (!inList) {
          result.push('<ul>');
          inList = true;
        }
        result.push(`<li>${trimmed.substring(2)}</li>`);
      } else {
        if (inList) {
          result.push('</ul>');
          inList = false;
        }
        result.push(line);
      }
    }

    if (inList) {
      result.push('</ul>');
    }

    return result.join('\n');
  }

  private convertOrderedLists(html: string): string {
    const lines = html.split('\n');
    let inList = false;
    const result: string[] = [];

    for (const line of lines) {
      const trimmed = line.trim();
      if (trimmed.match(/^\d+\.\s/)) {
        if (!inList) {
          result.push('<ol>');
          inList = true;
        }
        result.push(`<li>${trimmed.replace(/^\d+\.\s/, '')}</li>`);
      } else {
        if (inList) {
          result.push('</ol>');
          inList = false;
        }
        result.push(line);
      }
    }

    if (inList) {
      result.push('</ol>');
    }

    return result.join('\n');
  }
}
